"use client";

import { useEffect, useState } from "react";
import useFetch from "@/hooks/useFetch";
import { getProductSkus, SkuAdminItem } from "@/requests/product";
import { createModeratorRequest } from "@/requests/moderator";
import PopupManager from "@/components/PopupManager";
import Loader from "@/components/Loader";
import { toastMe } from "@/utils";
import WarehouseStockForm from "./WarehouseStockForm";

interface EditableDiscount {
  discount_type: string;
  discount_amount: number;
  discount_price: number;
  discount_start_date: string;
  discount_end_date: string;
  status: number;
  priority: number;
}

interface SkuEditState {
  customSku: string;
  price: number;
  discountedType: string;
  discountedValue: number;
  discountedPrice: number;
  bufferStock: number;
  warehouseValues: Record<string, string>;
  discountPrices: EditableDiscount[];
}

interface UpdateSkuModalProps {
  productId: number;
  onClose: () => void;
}

const toLocalDatetime = (val: string | null | undefined) => {
  if (!val) return "";
  // "2026-07-30T10:00:00.000Z" → "2026-07-30T10:00"
  return val.length >= 16 ? val.slice(0, 16) : val;
};

const calculateDiscountedPrice = (
  price: number,
  discountType: string,
  discountValue: number,
) => {
  const discount =
    discountType === "percentage"
      ? (price * discountValue) / 100
      : discountValue;

  return Math.max(Math.round(price - discount), 0);
};

const UpdateSkuModal = ({ productId, onClose }: UpdateSkuModalProps) => {
  const res = useFetch(() => getProductSkus(productId), {
    deps: [productId],
  });

  const skuList: SkuAdminItem[] = res.response?.Data || [];

  // Local edit state for each SKU
  const [editState, setEditState] = useState<Record<number, SkuEditState>>({});

  // SKU currently being submitted for moderation
  const [savingSkuId, setSavingSkuId] = useState<number | null>(null);

  // Initialise edit state when data loads
  useEffect(() => {
    if (skuList.length > 0) {
      const state: Record<number, SkuEditState> = {};
      for (const sku of skuList) {
        const warehouseValues: Record<string, string> = {};
        for (const ws of sku.warehouseSkusStock || []) {
          warehouseValues[ws.warehouseId] = String(ws.qty);
        }
        state[sku.id] = {
          customSku: (sku as any).customSku || (sku as any).custom_sku || "",
          price: sku.price,
          discountedType:
            sku.discountedType || (sku as any).discounted_type || "amount",
          discountedValue:
            sku.discountedValue ?? (sku as any).discounted_value ?? 0,
          discountedPrice:
            sku.discountedPrice ?? (sku as any).discounted_price ?? 0,
          bufferStock: (sku as any).bufferStock ?? (sku as any).buffer_stock ?? 0,
          warehouseValues,
          discountPrices: (sku.discountPrices || []).map((dp) => ({
            discount_type: dp.discount_type || "fixed",
            discount_amount: dp.discount_amount || 0,
            discount_price: dp.discount_price ?? 0,
            discount_start_date: toLocalDatetime(dp.discount_start_date),
            discount_end_date: toLocalDatetime(dp.discount_end_date),
            status: dp.status ?? 1,
            priority: dp.priority ?? 0,
          })),
        };
      }
      setEditState(state);
    }
  }, [skuList]);

  // Expanded SKU accordion tracking
  const [expandedSkus, setExpandedSkus] = useState<Set<number>>(new Set());

  const toggleExpand = (skuId: number) => {
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };

  // Attribute display helpers
  const skuAttributesLabel = (sku: SkuAdminItem) => {
    if (!sku.attributes || sku.attributes.length === 0) return "—";
    return sku.attributes
      .map((attr) => `${attr.key}: ${attr.value}`)
      .join(", ");
  };

  // ── Helpers ──
  const updateSku = (skuId: number, patch: Partial<SkuEditState>) => {
    setEditState((prev) => {
      const current = prev[skuId];
      if (!current) return prev;
      return { ...prev, [skuId]: { ...current, ...patch } };
    });
  };

  const updateDiscount = (
    skuId: number,
    index: number,
    patch: Partial<EditableDiscount>,
  ) => {
    setEditState((prev) => {
      const current = prev[skuId];
      if (!current) return prev;
      const dps = [...current.discountPrices];
      dps[index] = { ...dps[index], ...patch };
      return { ...prev, [skuId]: { ...current, discountPrices: dps } };
    });
  };

  // Handle saving a single SKU via the moderation request flow
  const handleSave = async (skuId: number) => {
    const state = editState[skuId];
    if (!state) return;

    const totalStock = Object.values(state.warehouseValues).reduce(
      (sum, v) => sum + (parseInt(v) || 0),
      0,
    );

    const payload = {
      customSku: state.customSku,
      price: state.price,
      discountedType: state.discountedType,
      discountedValue: state.discountedValue,
      discountedPrice: state.discountedPrice,
      bufferStock: state.bufferStock,
      stockQuantity: totalStock,
      warehouseStocks: Object.entries(state.warehouseValues).map(
        ([warehouseId, qty]) => ({
          warehouseId: Number(warehouseId),
          qty: parseInt(qty) || 0,
        }),
      ),
      discountPrices: state.discountPrices.map((dp) => ({
        discount_type: dp.discount_type,
        discount_amount: dp.discount_amount,
        discount_price: dp.discount_price,
        discount_start_date: dp.discount_start_date || null,
        discount_end_date: dp.discount_end_date || null,
      })),
    };

    setSavingSkuId(skuId);
    try {
      const res = await createModeratorRequest({
        entity_type: "Sku",
        entity_id: skuId,
        action_type: "update",
        requested_updated_data: payload,
      });

      if (res.Success) {
        toastMe(res.Message || "SKU update request submitted for moderation");
      } else {
        toastMe(
          res.Message || "Failed to submit SKU update request",
          "error",
        );
      }
    } catch (err) {
      console.error("Error submitting SKU update request:", err);
      toastMe(err, "error");
    } finally {
      setSavingSkuId(null);
    }
  };

  // ── Render ──

  if (res.loading) {
    return (
      <PopupManager onClose={onClose}>
        <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-[800px] max-h-[85vh] overflow-y-auto p-8 flex items-center justify-center">
          <Loader />
        </div>
      </PopupManager>
    );
  }

  if (res.error) {
    return (
      <PopupManager onClose={onClose}>
        <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-[500px] p-8">
          <p className="text-red-600 font-medium text-center">
            Failed to load SKU data. Please try again.
          </p>
          <div className="flex justify-center mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </PopupManager>
    );
  }

  return (
    <PopupManager onClose={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-[1000px] max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-800">
            Update SKU — Product #{productId}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {skuList.length} SKU{skuList.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {/* SKU list */}
        <div className="p-6 space-y-4">
          {skuList.length === 0 && (
            <p className="text-center text-gray-400 py-8">No SKUs found.</p>
          )}

          {skuList.map((sku) => {
            const state = editState[sku.id];
            if (!state) return null;

            const isExpanded = expandedSkus.has(sku.id);
            const totalWarehouseQty = Object.values(
              state.warehouseValues,
            ).reduce((sum, v) => sum + (parseInt(v) || 0), 0);

            return (
              <div
                key={sku.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* SKU summary row */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpand(sku.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">
                        {state.customSku || sku.sku || `SKU #${sku.id}`}
                      </span>
                      {skuAttributesLabel(sku) !== "—" && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {skuAttributesLabel(sku)}
                        </span>
                      )}
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded ${
                          sku.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {sku.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span>
                        Price:{" "}
                        <strong className="text-gray-700">
                          ৳{state.price}
                        </strong>
                      </span>
                      <span>
                        Buffer:{" "}
                        <strong className="text-orange-600">
                          {state.bufferStock}
                        </strong>
                      </span>
                      <span>
                        Stock:{" "}
                        <strong className="text-gray-700">
                          {totalWarehouseQty}
                        </strong>
                      </span>
                      <span>
                        Discounts:{" "}
                        <strong className="text-blue-600">
                          {state.discountPrices.length}
                        </strong>
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {/* Expanded edit area */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/50 space-y-5">
                    {/* ── Row 1: Custom SKU + Price + Buffer ── */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Custom SKU
                        </label>
                        <input
                          type="text"
                          value={state.customSku}
                          onChange={(e) =>
                            updateSku(sku.id, { customSku: e.target.value })
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Price (৳)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={state.price}
                          onChange={(e) => {
                            const price = Number(e.target.value);
                            updateSku(sku.id, {
                              price,
                              discountedPrice: calculateDiscountedPrice(
                                price,
                                state.discountedType,
                                state.discountedValue,
                              ),
                            });
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Buffer Stock
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={state.bufferStock}
                          onChange={(e) =>
                            updateSku(sku.id, {
                              bufferStock: Number(e.target.value),
                            })
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          SKU Discount Type
                        </label>
                        <select
                          value={state.discountedType}
                          onChange={(e) => {
                            const discountedType = e.target.value;
                            updateSku(sku.id, {
                              discountedType,
                              discountedPrice: calculateDiscountedPrice(
                                state.price,
                                discountedType,
                                state.discountedValue,
                              ),
                            });
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        >
                          <option value="amount">Amount (৳)</option>
                          <option value="percentage">Percentage (%)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          SKU Discount Value
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={state.discountedValue}
                          onChange={(e) => {
                            const discountedValue = Number(e.target.value);
                            updateSku(sku.id, {
                              discountedValue,
                              discountedPrice: calculateDiscountedPrice(
                                state.price,
                                state.discountedType,
                                discountedValue,
                              ),
                            });
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          SKU Discounted Price (৳)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={state.discountedPrice}
                          onChange={(e) =>
                            updateSku(sku.id, {
                              discountedPrice: Number(e.target.value),
                            })
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                    </div>

                    {/* ── Row 2: Warehouse Stock ── */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Warehouse Stock Quantities
                      </label>
                      <div className="bg-white border border-gray-200 rounded-md p-3">
                        <WarehouseStockForm
                          warehouseStocks={sku.warehouseSkusStock || []}
                          values={state.warehouseValues}
                          onChange={(warehouseId, value) => {
                            updateSku(sku.id, {
                              warehouseValues: {
                                ...state.warehouseValues,
                                [warehouseId]: value,
                              },
                            });
                          }}
                        />
                      </div>
                    </div>

                    {/* ── Row 3: Discount Prices ── */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Discount Prices
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            updateSku(sku.id, {
                              discountPrices: [
                                ...state.discountPrices,
                                {
                                  discount_type: "fixed",
                                  discount_amount: 0,
                                  discount_price: state.price,
                                  discount_start_date: "",
                                  discount_end_date: "",
                                  status: 1,
                                  priority:
                                    state.discountPrices.length +
                                    1,
                                },
                              ],
                            });
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Add Discount
                        </button>
                      </div>

                      {state.discountPrices.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-3 bg-white border border-gray-200 rounded-md">
                          No discount prices
                        </p>
                      )}

                      {state.discountPrices.map((dp, i) => (
                        <div
                          key={i}
                          className="bg-white border border-gray-200 rounded-md p-3 mb-2"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700">
                              Discount #{i + 1}
                              <span className="ml-2 text-gray-400 font-normal">
                                (priority {dp.priority})
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const dps = [...state.discountPrices];
                                dps.splice(i, 1);
                                updateSku(sku.id, {
                                  discountPrices: dps,
                                });
                              }}
                              className="text-xs text-red-500 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                Type
                              </label>
                              <select
                                value={dp.discount_type}
                                onChange={(e) =>
                                  updateDiscount(sku.id, i, {
                                    discount_type: e.target.value,
                                  })
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              >
                                <option value="fixed">Fixed (৳)</option>
                                <option value="percentage">
                                  Percentage (%)
                                </option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                Amount
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={dp.discount_amount}
                                onChange={(e) => {
                                  const amount = Number(e.target.value);
                                  const discountPrice =
                                    dp.discount_type === "percentage"
                                      ? Math.round(
                                          state.price -
                                            (state.price * amount) / 100,
                                        )
                                      : state.price - amount;
                                  updateDiscount(sku.id, i, {
                                    discount_amount: amount,
                                    discount_price:
                                      discountPrice > 0
                                        ? discountPrice
                                        : 0,
                                  });
                                }}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                Priority
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={dp.priority}
                                onChange={(e) =>
                                  updateDiscount(sku.id, i, {
                                    priority: Number(e.target.value),
                                  })
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                Start
                              </label>
                              <input
                                type="datetime-local"
                                value={dp.discount_start_date}
                                onChange={(e) =>
                                  updateDiscount(sku.id, i, {
                                    discount_start_date: e.target.value,
                                  })
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                End
                              </label>
                              <input
                                type="datetime-local"
                                value={dp.discount_end_date}
                                onChange={(e) =>
                                  updateDiscount(sku.id, i, {
                                    discount_end_date: e.target.value,
                                  })
                                }
                                className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                          </div>

                          <div className="mt-2 text-[11px] text-gray-400">
                            Result:{" "}
                            <span className="font-semibold text-green-600">
                              ৳{dp.discount_price}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Save button */}
                    <div className="flex justify-end pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleSave(sku.id)}
                        disabled={savingSkuId === sku.id}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-md transition-colors"
                      >
                        {savingSkuId === sku.id
                          ? "Submitting..."
                          : `Save SKU #${sku.id}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PopupManager>
  );
};

export default UpdateSkuModal;
